import { useState, useEffect, useCallback, useRef } from 'react';
import createSdk from '@descope/web-js-sdk';
import type { AuthState } from '@/types/auth';
import { decodeJWT, isTokenExpired, getTokenExpiration } from '@/utils/jwt';

const DESCOPE_PROJECT_ID = import.meta.env.VITE_DESCOPE_PROJECT_ID || '';
const DESCOPE_BASE_URL = import.meta.env.VITE_DESCOPE_BASE_URL || 'https://api.descope.com';

// Type for SDK with persistTokens: true which includes getSessionToken/getRefreshToken
type DescopeSdk = ReturnType<typeof createSdk<true>>;

/**
 * Custom hook for managing Descope authentication
 */
export function useDescope() {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    error: null,
  });

  const sdkRef = useRef<DescopeSdk | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Schedule automatic token refresh before expiration
   */
  const scheduleTokenRefresh = useCallback((token: string) => {
    const expiration = getTokenExpiration(token);
    if (!expiration) {
      return;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    const timeUntilExpiry = expiration - currentTime;

    // Refresh 5 minutes before expiration
    const refreshTime = Math.max(0, (timeUntilExpiry - 300) * 1000);

    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    refreshTimerRef.current = setTimeout(() => {
      refreshSession();
    }, refreshTime);
  }, []);

  /**
   * Refresh the session token
   */
  const refreshSession = useCallback(async () => {
    if (!sdkRef.current) {
      return;
    }

    try {
      const response = await sdkRef.current.refresh();

      if (response.ok && response.data?.sessionJwt) {
        const userInfo = decodeJWT(response.data.sessionJwt);
        setAuthState({
          isAuthenticated: true,
          isLoading: false,
          user: userInfo,
          error: null,
        });

        // Schedule next refresh
        scheduleTokenRefresh(response.data.sessionJwt);
      } else {
        throw new Error('Failed to refresh session');
      }
    } catch (error) {
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        error: error instanceof Error ? error : new Error('Session refresh failed'),
      });
    }
  }, [scheduleTokenRefresh]);

  /**
   * Check if there's an existing valid session
   */
  const checkSession = useCallback(async () => {
    if (!sdkRef.current) {
      return;
    }

    try {
      const sessionToken = sdkRef.current.getSessionToken();
      const refreshToken = sdkRef.current.getRefreshToken();

      if (!sessionToken) {
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          error: null,
        });
        return;
      }

      // Check if token is expired
      if (isTokenExpired(sessionToken)) {
        if (refreshToken) {
          await refreshSession();
        } else {
          setAuthState({
            isAuthenticated: false,
            isLoading: false,
            user: null,
            error: null,
          });
        }
        return;
      }

      // Decode and set user info
      const userInfo = decodeJWT(sessionToken);
      setAuthState({
        isAuthenticated: true,
        isLoading: false,
        user: userInfo,
        error: null,
      });

      // Schedule token refresh
      scheduleTokenRefresh(sessionToken);
    } catch (error) {
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        error: error instanceof Error ? error : new Error('Session check failed'),
      });
    }
  }, [refreshSession, scheduleTokenRefresh]);

  /**
   * Handle magic link verification from URL
   */
  const handleMagicLinkVerification = useCallback(async () => {
    if (!sdkRef.current) {
      return false;
    }

    // Check for magic link token in URL
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('t');

    if (!token) {
      return false;
    }

    try {
      const response = await sdkRef.current.magicLink.verify(token);

      if (response.ok && response.data?.sessionJwt) {
        const userInfo = decodeJWT(response.data.sessionJwt);
        setAuthState({
          isAuthenticated: true,
          isLoading: false,
          user: userInfo,
          error: null,
        });

        // Schedule token refresh
        scheduleTokenRefresh(response.data.sessionJwt);

        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
        return true;
      } else {
        throw new Error('Magic link verification failed');
      }
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error : new Error('Magic link verification failed'),
      }));
      return false;
    }
  }, [scheduleTokenRefresh]);

  /**
   * Initialize Descope SDK
   */
  useEffect(() => {
    if (!DESCOPE_PROJECT_ID) {
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        error: new Error('Descope Project ID is not configured'),
      });
      return;
    }

    try {
      const sdk = createSdk({
        projectId: DESCOPE_PROJECT_ID,
        baseUrl: DESCOPE_BASE_URL,
        persistTokens: true,
        autoRefresh: true,
      });
      sdkRef.current = sdk;

      // First check for magic link token in URL, then check existing session
      const initAuth = async () => {
        const verified = await handleMagicLinkVerification();
        if (!verified) {
          await checkSession();
        }
      };

      initAuth();
    } catch (error) {
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        error: error instanceof Error ? error : new Error('Failed to initialize Descope'),
      });
    }

    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [checkSession, handleMagicLinkVerification]);

  /**
   * Sign in with magic link - sends email with link
   */
  const signInWithMagicLink = useCallback(async (email: string): Promise<void> => {
    if (!sdkRef.current) {
      throw new Error('Descope SDK not initialized');
    }

    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      // Use current URL as redirect URI for magic link
      const redirectUrl = window.location.origin + window.location.pathname;
      const response = await sdkRef.current.magicLink.signIn.email(email, redirectUrl);

      if (response.ok) {
        // Magic link sent successfully - user needs to click the link in their email
        setAuthState(prev => ({
          ...prev,
          isLoading: false,
          error: null,
        }));
      } else {
        throw new Error('Failed to send magic link');
      }
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error : new Error('Sign in failed'),
      }));
      throw error;
    }
  }, []);

  /**
   * Sign out the user
   */
  const signOut = useCallback(async (): Promise<void> => {
    if (!sdkRef.current) {
      return;
    }

    try {
      await sdkRef.current.logout();

      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }

      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        error: null,
      });
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        error: error instanceof Error ? error : new Error('Sign out failed'),
      }));
    }
  }, []);

  return {
    ...authState,
    signInWithMagicLink,
    signOut,
    refreshSession,
  };
}
